const GEORISK_HOST = "ulap-nga.georisk.gov.ph";
const BARANGAY_BOUNDARY_PATHS = new Set([
  "/arcgis/rest/services/PSA/BarangayPopMF/MapServer/0/query",
  "/arcgis/rest/services/PSA/Barangay_2020/MapServer/0/query",
]);
const BAROY_STATIC_BOUNDARY_URL =
  "https://raw.githubusercontent.com/faeldon/philippines-json-maps/master/2019/geojson/barangays/lowres/barangays-municity-ph103503000.0.001.json";

const ADMIN_EQUALITY = /\b(reg_name|prov_name|city_name|brgy_name)\s*=\s*'((?:''|[^'])*)'/gi;

type FetchGlobal = typeof globalThis & {
  __rbimBoundaryFetchFallbackInstalled?: boolean;
};

type StaticBoundaryFeature = {
  type?: string;
  geometry?: unknown;
  properties?: Record<string, unknown>;
};

type StaticBoundaryCollection = {
  type?: string;
  features?: StaticBoundaryFeature[];
};

function rewriteBoundaryWhereUppercaseLiterals(where: string) {
  return where.replace(
    ADMIN_EQUALITY,
    (_match, field: string, value: string) => `${field}='${value.toUpperCase()}'`,
  );
}

export function rewriteBoundaryWhereCaseInsensitive(where: string) {
  return where.replace(
    ADMIN_EQUALITY,
    (_match, field: string, value: string) =>
      `UPPER(${field})=UPPER('${value}')`,
  );
}

function rewriteBoundaryWhereMunicipalityPrefix(where: string) {
  return rewriteBoundaryWhereUppercaseLiterals(where).replace(
    /\bcity_name\s*=\s*'((?:''|[^'])*)'/i,
    (_match, value: string) => {
      if (/^(CITY|MUNICIPALITY)\s+OF\s+/i.test(value)) {
        return `city_name='${value}'`;
      }
      return `city_name='MUNICIPALITY OF ${value}'`;
    },
  );
}

function boundaryUrlWithWhere(rawUrl: string, where: string) {
  const url = new URL(rawUrl);
  url.searchParams.set("where", where);
  return url.toString();
}

function isBarangayBoundaryUrl(rawUrl: string) {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  return (
    url.hostname === GEORISK_HOST &&
    BARANGAY_BOUNDARY_PATHS.has(url.pathname) &&
    Boolean(url.searchParams.get("where"))
  );
}

function simplifyAdministrativeWhere(where: string) {
  return where
    .replace(/UPPER\(\s*(reg_name|prov_name|city_name|brgy_name)\s*\)/gi, "$1")
    .replace(/UPPER\(\s*'((?:''|[^'])*)'\s*\)/gi, "'$1'");
}

function isBaroyLanaoDelNorteBoundaryQuery(rawUrl: string) {
  if (!isBarangayBoundaryUrl(rawUrl)) return false;

  const where = new URL(rawUrl).searchParams.get("where") ?? "";
  const normalized = simplifyAdministrativeWhere(where)
    .toUpperCase()
    .replace(/\s+/g, " ");

  return (
    /CITY_NAME\s*=\s*'BAROY'/.test(normalized) &&
    /PROV_NAME\s*=\s*'LANAO DEL NORTE'/.test(normalized)
  );
}

export function buildBoundaryFallbackUrls(rawUrl: string) {
  if (!isBarangayBoundaryUrl(rawUrl)) return [];

  const url = new URL(rawUrl);
  const where = url.searchParams.get("where");
  if (!where) return [];

  // GeoRisk stores a number of administrative labels in all caps. The original
  // RBIM query uses display labels such as "Baroy, Lanao del Norte", so first
  // retry the same scoped query with uppercase literal values. This avoids
  // relying on SQL functions that some ArcGIS query paths reject even with HTTP 200.
  const candidates = [
    rewriteBoundaryWhereUppercaseLiterals(where),
    rewriteBoundaryWhereMunicipalityPrefix(where),
    rewriteBoundaryWhereCaseInsensitive(where),
  ];

  return [...new Set(candidates)]
    .filter((candidate) => candidate !== where)
    .map((candidate) => boundaryUrlWithWhere(rawUrl, candidate));
}

export function buildCaseInsensitiveBoundaryUrl(rawUrl: string) {
  const fallbacks = buildBoundaryFallbackUrls(rawUrl);
  return fallbacks.find((candidate) => {
    const where = new URL(candidate).searchParams.get("where") ?? "";
    return /UPPER\(/i.test(where);
  }) ?? null;
}

function requestUrl(input: RequestInfo | URL) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function hasGeoJsonFeatures(value: unknown) {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { features?: unknown };
  return Array.isArray(candidate.features) && candidate.features.length > 0;
}

async function responseHasGeoJsonFeatures(response: Response) {
  try {
    return hasGeoJsonFeatures(await response.clone().json());
  } catch {
    return false;
  }
}

function staticText(properties: Record<string, unknown>, key: string) {
  const value = properties[key];
  return typeof value === "string" ? value : undefined;
}

function normalizeStaticPsgc(value: string | undefined) {
  return value?.replace(/^PH/i, "");
}

function normalizeBaroyStaticCollection(value: unknown) {
  if (!value || typeof value !== "object") return null;

  const collection = value as StaticBoundaryCollection;
  if (!Array.isArray(collection.features)) return null;

  const features = collection.features.flatMap((feature) => {
    if (feature.type !== "Feature" || !feature.geometry || !feature.properties) return [];

    const properties = feature.properties;
    const barangayName = staticText(properties, "ADM4_EN");
    const barangayCode = normalizeStaticPsgc(staticText(properties, "ADM4_PCODE"));
    const municipalityName = staticText(properties, "ADM3_EN");
    const municipalityCode = normalizeStaticPsgc(staticText(properties, "ADM3_PCODE"));
    const provinceName = staticText(properties, "ADM2_EN");
    const regionName = staticText(properties, "ADM1_EN");

    if (!barangayName || !barangayCode || municipalityName?.toUpperCase() !== "BAROY") {
      return [];
    }

    return [{
      type: "Feature",
      geometry: feature.geometry,
      properties: {
        reg_name: regionName,
        prov_name: provinceName,
        city_name: municipalityName,
        brgy_name: barangayName,
        brgy_code: barangayCode,
        city_code: municipalityCode,
        psgc_10d: barangayCode,
      },
    }];
  });

  return features.length
    ? { type: "FeatureCollection", features }
    : null;
}

async function loadBaroyStaticFallback(
  baseFetch: typeof fetch,
  rawUrl: string,
  init?: RequestInit,
) {
  if (!isBaroyLanaoDelNorteBoundaryQuery(rawUrl)) return null;

  try {
    const staticResponse = await baseFetch(BAROY_STATIC_BOUNDARY_URL, {
      signal: init?.signal,
      cache: "force-cache",
    });
    if (!staticResponse.ok) return null;

    const normalized = normalizeBaroyStaticCollection(await staticResponse.json());
    if (!normalized) return null;

    return new Response(JSON.stringify(normalized), {
      status: 200,
      headers: { "content-type": "application/geo+json" },
    });
  } catch {
    return null;
  }
}

export function createBoundaryAwareFetch(baseFetch: typeof fetch): typeof fetch {
  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await baseFetch(input, init);
    if (!response.ok) return response;

    const rawUrl = requestUrl(input);
    if (!isBarangayBoundaryUrl(rawUrl)) return response;

    // Keep the original successful response when it already contains boundaries.
    if (await responseHasGeoJsonFeatures(response)) return response;

    const fallbackUrls = buildBoundaryFallbackUrls(rawUrl);

    // Retry progressively without widening the user's authorized geographic scope:
    // 1) uppercase literal labels (the common PSA/GeoRisk representation),
    // 2) "MUNICIPALITY OF ..." naming, then
    // 3) a case-insensitive SQL expression as the final compatibility fallback.
    for (const fallbackUrl of fallbackUrls) {
      const fallbackResponse = await baseFetch(fallbackUrl, init);
      if (!fallbackResponse.ok) continue;
      if (await responseHasGeoJsonFeatures(fallbackResponse)) return fallbackResponse;
    }

    // GeoRisk can return HTTP 200 with zero Baroy features. This fallback must also
    // run for the newer Barangay_2020 service and for UPPER(...) queries generated by
    // the map component. Normalize the known PSGC-based Baroy GeoJSON to RBIM fields.
    const staticFallback = await loadBaroyStaticFallback(baseFetch, rawUrl, init);
    if (staticFallback) return staticFallback;

    return response;
  }) as typeof fetch;
}

export function installMapBoundaryFetchFallback() {
  const state = globalThis as FetchGlobal;
  if (
    state.__rbimBoundaryFetchFallbackInstalled ||
    typeof globalThis.fetch !== "function"
  ) {
    return;
  }

  globalThis.fetch = createBoundaryAwareFetch(globalThis.fetch.bind(globalThis));
  state.__rbimBoundaryFetchFallbackInstalled = true;
}

installMapBoundaryFetchFallback();
